<template>
  <BittsDrawer
    v-if="isReady"
    class="api-filter-drawer"
    :visible="true"
    :title="filter ? 'Filter settings' : 'Create filter'"
    width="600"
    @closed="handleClose"
  >
    <template #content>
      <div class="api-filter-drawer__body">
        <BittsContainer class="api-filter-drawer__identity">
          <BittsInput
            v-model="name"
            form-label="Filter name"
            name="api-filter-name"
            placeholder="Give this filter a name"
            :disabled="!canManage"
          />
          <div class="api-filter-drawer__id">
            <span class="api-filter-drawer__label">Filter ID</span>
            <ApiCopyValue
              v-if="filter"
              :value="filter.profile_id"
              :label="`ID for ${filter.name}`"
            />
            <BittsInput
              v-else
              model-value=""
              name="api-filter-id"
              placeholder="Generated after you save"
              :disabled="true"
            >
              <template #prefix>
                <FontAwesomeIcon
                  :icon="['fak', 'lock']"
                  class="text-neutral-text-placeholder"
                />
              </template>
            </BittsInput>
            <p class="api-filter-drawer__caption">{{ idCaption }}</p>
          </div>
        </BittsContainer>
        <section class="api-filter-drawer__populations">
          <div>
            <h3 class="api-filter-drawer__section-title">
              Customize Data in Push
            </h3>
            <p class="api-filter-drawer__section-description">
              Choose which of your populations and partner populations are
              exposed through this filter.
            </p>
          </div>
          <IntegrationSettings
            :population-settings="currentSettings"
            :setting-type="API_FILTER_INTEGRATION_TYPE"
            :hide-offline-partners="true"
            :is-disabled="isSaving || !canManage"
            @save:changes="onSettingsUpdated"
            @update:exclude-partnerships-created-since="
              onUpdateExcludePartnershipsCreatedSince
            "
          />
        </section>
      </div>
    </template>
    <template #footer>
      <ApiDrawerFooter
        :remove-text="filter ? 'Remove filter' : undefined"
        :is-remove-disabled="!canManage || isSaving"
        :is-saving="isSaving"
        :is-save-disabled="!canSave"
        @remove="handleRemoveClick"
        @save="handleSave"
      />
    </template>
  </BittsDrawer>

  <ApiFilterRemoveModal
    v-if="filterPendingRemove"
    :filter-id="filterPendingRemove.id"
    :filter-name="filterPendingRemove.name"
    @removed="handleRemoved"
    @closed="filterPendingRemove = null"
  />

  <UnsavedChangesModal
    v-if="isConfirmingDiscard"
    @discard="handleDiscard"
    @closed="isConfirmingDiscard = false"
  />
</template>

<script setup lang="ts">
import { BittsContainer, BittsDrawer, BittsInput } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useHead } from '@unhead/vue';
import { computed, onMounted, ref } from 'vue';

import IntegrationSettings from '@/components/integrations/IntegrationSettings.vue';
import UnsavedChangesModal from '@/components/integrations/UnsavedChangesModal.vue';

import { useIntegrationProfileStore } from '@/stores';
import { API_FILTER_INTEGRATION_TYPE } from '@/types/integration_profiles';

import ApiCopyValue from '../components/ApiCopyValue.vue';
import ApiDrawerFooter from '../components/ApiDrawerFooter.vue';
import ApiFilterRemoveModal from '../components/ApiFilterRemoveModal.vue';
import useApiFilterSettings from '../useApiFilterSettings';
import useApiRoutes from '../useApiRoutes';

const { resourceId = '' } = defineProps<{
  resourceId?: string;
}>();

const emit = defineEmits<{
  closed: [];
}>();

const integrationProfileStore = useIntegrationProfileStore();
const { openDetail } = useApiRoutes();

const {
  filter,
  name,
  currentSettings,
  canManage,
  hasUnsavedChanges,
  canSave,
  isSaving,
  initialize,
  onSettingsUpdated,
  onUpdateExcludePartnershipsCreatedSince,
  save,
} = useApiFilterSettings(resourceId);

useHead({
  title: computed(
    () =>
      `${filter.value?.name ?? 'Create filter'} - API Filters - API - Crossbeam`,
  ),
});

const isReady = ref(false);
const isConfirmingDiscard = ref(false);
const filterPendingRemove = ref<{ id: string; name: string } | null>(null);

const idCaption = computed(() =>
  filter.value
    ? 'Pass this ID with any API call, key, or integration to apply the filter.'
    : "You'll get an ID to pass with any API call, key, or integration once this filter is saved.",
);

function handleClose() {
  if (hasUnsavedChanges.value) isConfirmingDiscard.value = true;
  else emit('closed');
}

function handleDiscard() {
  isConfirmingDiscard.value = false;
  emit('closed');
}

async function handleSave() {
  const isCreating = !filter.value;
  const savedId = await save();
  if (!savedId) return;
  if (isCreating) openDetail('filters', savedId, { replace: true });
}

function handleRemoveClick() {
  if (!filter.value) return;
  filterPendingRemove.value = {
    id: filter.value.profile_id,
    name: filter.value.name || 'Untitled filter',
  };
}

function handleRemoved() {
  filterPendingRemove.value = null;
  isReady.value = false;
  emit('closed');
}

onMounted(async () => {
  await integrationProfileStore.readySync;
  // A stale or deleted id would otherwise leave the route open with nothing
  // to dismiss.
  if (resourceId && !filter.value) {
    emit('closed');
    return;
  }
  initialize();
  isReady.value = true;
});
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.api-filter-drawer__body {
  @apply flex flex-col gap-24 p-16;
}

.api-filter-drawer__identity {
  @apply flex flex-col gap-16 bg-neutral-background-weak border-0;
}

.api-filter-drawer__id {
  @apply flex flex-col gap-8;
}

.api-filter-drawer__label {
  @apply text-sm font-bold text-neutral-text-strong;
}

.api-filter-drawer__caption {
  @apply text-sm text-neutral-text-weak m-0;
}

.api-filter-drawer__populations {
  @apply flex flex-col gap-16;
}

.api-filter-drawer__section-title {
  @apply text-base font-bold text-neutral-text-strong m-0;
}

.api-filter-drawer__section-description {
  @apply text-sm text-neutral-text m-0 mt-4;
}
</style>
